import React, { memo, useCallback } from 'react'
import {
  Aerobar,
  Button,
  Checkbox,
  Loader,
  Modal,
  Tag,
  TextInput,
  Tooltip,
} from '@grofers/dock-design-kit'
import { imageUrls, MAX_VISIBLE_BRANDS_IN_MODAL } from '../../../constants/appConfig'
import ErrorOutlinedIcon from '../../../assets/svg/errorOutlined.svg?react'
import { BRAND_SELECTION_MODAL_TEXT } from '../../../constants/commonConstants'
import { ExpandableBrandList } from '../../ui/ExpandableBrandList'
import type { BrandListItemProps, BrandSelectionModalProps } from './BrandSelectionModal.types'
import { useBrandSelectionModalData } from '@/hooks/brand/useBrandSelectionModalData'
import useScrollbarVisibility from '@/hooks/common/useScrollbarVisibility'

const BrandListItem: React.FC<BrandListItemProps> = memo(
  ({ brand, isSelected, isDisabled, onToggle }) => {
    const isInteractionDisabled = brand.hasAccess || (isDisabled && !isSelected)
    const handleToggle = useCallback(() => onToggle(brand), [onToggle, brand])
    const stopPropagation = useCallback((e: React.MouseEvent) => e.stopPropagation(), [])

    return (
      <div
        className={`flex items-center px-3 py-3 ${
          isInteractionDisabled
            ? 'cursor-not-allowed opacity-60'
            : 'cursor-pointer hover:bg-gray-50'
        }`}
        onClick={handleToggle}
      >
        {brand.logo ? (
          <img
            src={brand.logo}
            alt={brand.name}
            className="mr-3 h-8 w-8 shrink-0 rounded-lg border border-gray-200 object-contain"
          />
        ) : (
          <div className="mr-3 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-gray-50">
            <span className="text-xs font-medium text-gray-500">
              {brand.name.charAt(0).toUpperCase()}
            </span>
          </div>
        )}
        <span className="flex-1 text-sm text-gray-900">{brand.name}</span>
        {brand.hasAccess ? (
          <Tag
            label={BRAND_SELECTION_MODAL_TEXT.alreadyHaveAccessTag}
            color="grey"
            size="s"
            variant="secondary"
          />
        ) : null}
        <div className="ml-3 flex h-4 w-4 shrink-0 items-center justify-center">
          <div onClick={stopPropagation}>
            <Checkbox
              isSelected={isSelected}
              onChange={handleToggle}
              disabled={isInteractionDisabled}
              size="small"
              label=""
            />
          </div>
        </div>
      </div>
    )
  }
)

const BrandSelectionModal: React.FC<BrandSelectionModalProps> = (props) => {
  const {
    titleText,
    isOpen = false,
    onClose,
    onRequestAccess = () => {},
    isLoading = false,
    rejectionReason = '',
    requestedBrands = [],
    subText,
    dismissable = true,
  } = props

  const {
    common: { blinkitRoundLogo },
  } = imageUrls

  const {
    state: {
      selectedBrands,
      searchTerm,
      apiError,
      isBrandsExpanded,
      isRequestButtonEnabled,
      isRequestingAccess,
      brandFetchError,
      isInitialBrandsLoading,
      shouldShowEmptyState,
      shouldShowLoadMoreSpinner,
      processingButtonLabel,
      requestAccessButtonLabel,
      viewOnlySelected,
      displayedBrands,
      maxBrandsPerRequest,
      isAtLimit,
    },
    action: {
      handleBrandToggle,
      handleRequestAccess,
      handleInputChange,
      handleExpandBrands,
      handleCollapseBrands,
      handleToggleViewSelected,
      loadMoreRef,
    },
  } = useBrandSelectionModalData({ isOpen, onClose, onRequestAccess, isLoading })

  const {
    blinkitLogoAlt,
    title,
    subtitle,
    searchPlaceholder,
    brandSelectionModalSubtitle,
    selectedBrandsAerobarContent,
    viewOnlySelectedLabel,
    brandFetchError: brandFetchErrorText,
  } = BRAND_SELECTION_MODAL_TEXT

  const AerobarContent = selectedBrandsAerobarContent(maxBrandsPerRequest)

  const selectedCount = selectedBrands?.length ?? 0

  const { isScrollbarVisible, scrollContainerRef } = useScrollbarVisibility([
    displayedBrands.length,
    isInitialBrandsLoading,
    shouldShowLoadMoreSpinner,
    brandFetchError,
    viewOnlySelected,
  ])

  const handleRequestAccessClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      handleRequestAccess()
    },
    [handleRequestAccess]
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      aria-label="Brand selection modal"
      contentClassName="overflow-hidden px-6 pb-6 pt-6"
      showClose={dismissable}
      closeOnBackdropClick={dismissable}
    >
      <div className="flex justify-center pb-3 pt-4">
        <img src={blinkitRoundLogo} alt={blinkitLogoAlt} className="h-16 w-16" />
      </div>
      <div className="flex flex-col items-center px-8">
        <div className="text-center text-bodySm font-medium text-custom-textPrimary">
          {subText ?? subtitle}
        </div>
        {rejectionReason && (
          <Tooltip content={rejectionReason} position="bottom-center" showArrow multiLine>
            <div className="mt-1 w-full truncate text-center text-bodySm font-normal text-custom-textSubtle">
              {rejectionReason}
            </div>
          </Tooltip>
        )}
        {requestedBrands?.length > 0 ? (
          <ExpandableBrandList
            brands={requestedBrands}
            isExpanded={isBrandsExpanded}
            onExpand={handleExpandBrands}
            onCollapse={handleCollapseBrands}
            maxVisible={MAX_VISIBLE_BRANDS_IN_MODAL}
            className="mt-3 justify-center"
          />
        ) : null}
        <div className="mt-1 text-center text-xl font-bold text-gray-900">{titleText || title}</div>
        <div className="text-center text-bodySm font-normal text-custom-textSubtle">
          {brandSelectionModalSubtitle}
        </div>
      </div>

      <div className="my-4 rounded-lg border border-gray-200">
        <TextInput
          placeholder={searchPlaceholder}
          value={searchTerm}
          onChange={handleInputChange}
          leftIcon="search"
          fullWidth
          size="md"
          disabled={viewOnlySelected}
          className="mt-4 px-4"
        />
        <div className="flex items-center justify-between border-b border-gray-200 py-3 pl-4">
          <span className="text-xs text-gray-600">
            {viewOnlySelectedLabel} ({selectedCount})
          </span>
          <Checkbox
            isSelected={viewOnlySelected}
            onChange={handleToggleViewSelected}
            disabled={selectedCount === 0}
            size="small"
            label=""
            className="pr-[1.2rem]"
          />
        </div>
        {brandFetchError && !isInitialBrandsLoading ? (
          <div className="px-4 py-4 text-left text-xs text-red-error">{brandFetchErrorText}</div>
        ) : (
          <div
            ref={scrollContainerRef}
            className={`custom-scrollbar h-60 overflow-y-auto ${!isScrollbarVisible ? 'pr-[0.4rem]' : ''}`}
          >
            {displayedBrands?.map((brand) => (
              <BrandListItem
                key={brand.id}
                brand={brand}
                isSelected={selectedBrands?.some((s) => s.id === brand.id)}
                isDisabled={isAtLimit}
                onToggle={handleBrandToggle}
              />
            ))}
            {isInitialBrandsLoading ? (
              <div className="flex h-full items-center justify-center">
                <Loader showLoader />
              </div>
            ) : null}
            {shouldShowEmptyState && !brandFetchError ? (
              <div className="p-3 text-left text-sm text-gray-500">No brands found</div>
            ) : null}
            {shouldShowLoadMoreSpinner ? (
              <div className="flex justify-center p-3">
                <Loader showLoader />
              </div>
            ) : null}
            <div ref={loadMoreRef} className="h-px w-full" />
          </div>
        )}
      </div>
      <Aerobar
        hierarchy="low"
        variant="discover"
        iconName="info-2"
        iconSize="lg"
        headlineContent={AerobarContent}
        showClose={false}
        isFullWidth={true}
      />

      {apiError && (
        <div className="mt-3 flex items-center gap-2 rounded border border-red-200 bg-red-50 p-3">
          <ErrorOutlinedIcon className="h-4 w-4" />
          <span className="text-sm text-red-error">{apiError}</span>
        </div>
      )}
      <Button
        type="button"
        variant="primary"
        size="large"
        fullWidth
        onClick={handleRequestAccessClick}
        disabled={!isRequestButtonEnabled}
        isLoading={isLoading || isRequestingAccess}
        className="mt-4"
      >
        {isLoading || isRequestingAccess ? processingButtonLabel : requestAccessButtonLabel}
      </Button>
    </Modal>
  )
}

export default BrandSelectionModal
